import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detecta si el DialogContent tiene más contenido debajo del viewport.
 * `resetKey` debe cambiar cuando el layout del contenido pueda crecer/achicarse.
 */
export function useShareDialogScrollHint(resetKey = '') {
  const contentRef = useRef(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  const update = useCallback(() => {
    const el = contentRef.current;
    if (!el) {
      setCanScrollMore(false);
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    setCanScrollMore(remaining > 8);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      setCanScrollMore(false);
      return undefined;
    }

    const rafId = window.requestAnimationFrame(update);
    el.addEventListener('scroll', update, { passive: true });

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [update, resetKey]);

  return { contentRef, canScrollMore, refreshScrollHint: update };
}

/** Sombra superior del footer cuando hay más contenido scrolleable abajo. */
export function getShareDialogFooterScrollHintSx(canScrollMore) {
  return {
    position: 'relative',
    flexShrink: 0,
    borderTop: canScrollMore
      ? '1px solid rgba(255,255,255,0.08)'
      : '1px solid transparent',
    boxShadow: canScrollMore
      ? '0 -14px 28px rgba(0,0,0,0.55)'
      : 'none',
    transition: 'box-shadow 160ms ease, border-color 160ms ease',
    '&::before': canScrollMore
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          top: -28,
          height: 28,
          pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(26,26,26,0), rgba(26,26,26,0.92))',
        }
      : undefined,
  };
}
